import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Plus, Search, Eye } from 'lucide-react';
import { usePurchaseOrderStore, useSupplierStore } from '../../hooks';
import { Button, Card, CardContent, Badge } from '@lancar-pos/ui';

const statusColors: Record<string, 'default' | 'warning' | 'success' | 'danger' | 'primary'> = {
  draft: 'default',
  pending: 'warning',
  confirmed: 'primary',
  received: 'success',
  cancelled: 'danger',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  pending: 'Menunggu',
  confirmed: 'Dikonfirmasi',
  received: 'Diterima',
  cancelled: 'Dibatalkan',
};

export function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const { purchaseOrders } = usePurchaseOrderStore();
  const { suppliers } = useSupplierStore();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filteredOrders = useMemo(() => {
    return purchaseOrders
      .filter((po) => !po._deleted)
      .filter((po) => {
        if (statusFilter !== 'all' && po.status !== statusFilter) return false;
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return po.orderNumber.toLowerCase().includes(query);
      })
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [purchaseOrders, statusFilter, searchQuery]);

  const getSupplierName = (supplierId: string) => {
    const supplier = suppliers.find((s) => s.id === supplierId);
    return supplier?.name || '-';
  };

  const handleAddOrder = () => {
    window.location.href = '/app/inventory/purchase-orders/new';
  };

  const handleViewOrder = (id: string) => {
    window.location.href = `/app/inventory/purchase-orders/${id}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate({ to: '/app' })}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Pembelian</h1>
          </div>
          <Button onClick={handleAddOrder}>
            <Plus className="w-4 h-4 mr-2" />Pesanan Pembelian
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 lg:p-6 space-y-4">
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Cari pesanan..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {['all', 'draft', 'pending', 'confirmed', 'received', 'cancelled'].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === status
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status === 'all' ? 'Semua' : statusLabels[status]}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">No. PO</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        {searchQuery || statusFilter !== 'all' ? 'Pesanan tidak ditemukan' : 'Belum ada pesanan pembelian'}
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map((order) => (
                      <tr 
                        key={order.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleViewOrder(order.id)}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{order.orderNumber}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{getSupplierName(order.supplierId)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {new Date(order.createdAt).toLocaleDateString('id-ID')}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          Rp {order.totalAmount.toLocaleString('id-ID')}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={statusColors[order.status]}>
                            {statusLabels[order.status]}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewOrder(order.id);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                          >
                            <Eye className="w-4 h-4 text-gray-600" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
